import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, setApiToken } from './config';
import Login from './Login';
import AppRouter from './AppRouter';
import './App.css';

function App() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Handle navigation from user menu
  const handleShowSettings = () => {
    navigate('/settings');
  };
  
  const handleShowTags = () => {
    navigate('/tags');
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/data/`);
      setData(response.data.results || response.data);
      setError(null);
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        handleLogout();
        setError('Session expired. Please log in again.');
      } else {
        setError('Error fetching data: ' + err.message);
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (loggedInUsername) => {
    setIsLoggedIn(true);
    setUsername(loggedInUsername);
    setLoading(false);
    
    // Reset viewing_as on login
    try {
      await axios.post(`${API_URL}/portfolio/switch-user/`, {
        username: loggedInUsername
      });
    } catch (err) {
      console.error('Error resetting viewing_as on login:', err);
    }
    
    fetchData();
    navigate('/equity');
  };

  const handleLogout = () => {
    localStorage.removeItem('api_username');
    localStorage.removeItem('api_password');
    localStorage.removeItem('api_token');
    localStorage.removeItem('api_session_expires');
    setApiToken(null);
    setIsLoggedIn(false);
    setUsername('');
    setData([]);
    setError(null);
    navigate('/');
  };

  useEffect(() => {
    const savedUsername = localStorage.getItem('api_username');
    const savedToken = localStorage.getItem('api_token');
    const savedPassword = localStorage.getItem('api_password');
    const sessionExpires = localStorage.getItem('api_session_expires');
    const isSessionValid = sessionExpires && parseInt(sessionExpires, 10) > Date.now();

    const clearStoredAuth = () => {
      localStorage.removeItem('api_username');
      localStorage.removeItem('api_password');
      localStorage.removeItem('api_token');
      localStorage.removeItem('api_session_expires');
    };

    const bootstrapWithToken = (token, username) => {
      setApiToken(token);
      axios
        .get(`${API_URL}/portfolio/accounts/`)
        .then(() => {
          setIsLoggedIn(true);
          setUsername(username);
          fetchData();
        })
        .catch(() => {
          clearStoredAuth();
          setApiToken(null);
          setLoading(false);
        });
    };

    if (savedUsername && savedToken && isSessionValid) {
      bootstrapWithToken(savedToken, savedUsername);
    } else if (savedUsername && savedPassword && isSessionValid) {
      axios
        .post(`${API_URL}/auth/token/`, {
          username: savedUsername,
          password: savedPassword,
        })
        .then((res) => {
          const token = res.data.token;
          localStorage.setItem('api_token', token);
          localStorage.removeItem('api_password');
          bootstrapWithToken(token, savedUsername);
        })
        .catch(() => {
          clearStoredAuth();
          setLoading(false);
        });
    } else {
      if (savedUsername || savedPassword || savedToken) {
        clearStoredAuth();
      }
      setApiToken(null);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // Toon login pagina als niet ingelogd
  if (!isLoggedIn) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <AppRouter
      username={username}
      onLogout={handleLogout}
      onManageAccounts={handleShowSettings}
      onManageTags={handleShowTags}
      data={data}
      loading={loading}
      error={error}
      fetchData={fetchData}
    />
  );
}

export default App;

